import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Heart, Target, Users, Lightbulb, Shield, 
  Zap, Award
} from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Pragmatisme',
    description: 'On ne fait que ce qui est utile, justifié et aligné avec vos objectifs. Pas de gadgets, du concret.',
  },
  {
    icon: Users,
    title: 'Partenariat',
    description: 'On n\'est pas un prestataire, on est un partenaire. Votre réussite est notre réussite.',
  },
  {
    icon: Lightbulb,
    title: 'Clarté',
    description: 'Pas de jargon technique. On explique simplement, on agit efficacement.',
  },
  {
    icon: Shield,
    title: 'Transparence',
    description: 'Des prix clairs, des délais respectés, une communication honnête.',
  },
];

const stats = [
  { value: '50+', label: 'Clients accompagnés' },
  { value: '95%', label: 'Satisfaction client' },
  { value: '20 ans', label: 'D\'expérience' },
  { value: '200+', label: 'Projets réalisés' },
];

export default function AProposPage() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />

        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              ref={headerRef}
              initial={{ opacity: 0, y: 30 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label mb-4 inline-block">
                À propos
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Votre partenaire de{' '}
                <span className="gradient-text">croissance digitale</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                PULSIA est un cabinet de stratégie digitale et d'architecture IA. 
                Nous accompagnons les dirigeants de TPE/PME depuis l'analyse des besoins 
                jusqu'à l'implémentation concrète de solutions digitales, IA et automatisées.
              </p>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-card/50 border border-border/50">
                <Heart className="w-8 h-8 text-primary" />
                <p className="text-sm text-muted-foreground">
                  Basé à <span className="text-foreground font-medium">La Réunion</span>, 
                  nous accompagnons des entreprises dans toute la France.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={headerInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-border/50 shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1532622785990-d2c36a76f5a6?w=800&q=80"
                  alt="Équipe Pulsia"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4, scale: 1.02 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl card-gradient border border-border/50 text-center"
              >
                <p className="font-display text-3xl lg:text-4xl font-bold gradient-text mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label mb-4 inline-block">
                Notre Mission
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Aider les TPE à{' '}
                <span className="gradient-text">structurer</span>
                {' '}leur croissance digitale
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Nous aidons les dirigeants de TPE à structurer une stratégie digitale cohérente, 
                rentable et adaptée à leur réalité, en combinant vision stratégique et solutions 
                concrètes (marketing digital, IA, automatisation).
              </p>
              <div className="space-y-4">
                {[
                  'Générer plus de chiffre d\'affaires',
                  'Fluidifier le parcours client',
                  'Gagner du temps opérationnel',
                  'Investir dans les bons outils, au bon moment',
                  'Prendre de meilleures décisions digitales',
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <Zap className="w-5 h-5 text-primary shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden border border-border/50 shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1653549893012-b8b4fbe97630?w=800&q=80"
                  alt="Innovation digitale"
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="section-label mb-4 inline-block">
              Nos Valeurs
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Ce qui nous{' '}
              <span className="gradient-text">guide</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4, scale: 1.02 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl card-gradient border border-border/50 hover:border-primary/30 transition-all text-center"
              >
                <div className="feature-icon mx-auto mb-4">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We're Not Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-12">
              <span className="section-label mb-4 inline-block">
                Notre positionnement
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
                Ce qu'on n'est{' '}
                <span className="text-destructive">pas</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-destructive/5 border border-destructive/20">
                <h3 className="font-medium text-destructive mb-4">On n'est pas...</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>❌ Un vendeur d'outils</li>
                  <li>❌ Une agence web classique</li>
                  <li>❌ Un prestataire jetable</li>
                  <li>❌ Des techniciens qui parlent jargon</li>
                </ul>
              </div>
              <div className="p-6 rounded-xl bg-success/5 border border-success/20">
                <h3 className="font-medium text-success mb-4">On est...</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>✓ Un stratège digital</li>
                  <li>✓ Un architecte de systèmes</li>
                  <li>✓ Un partenaire de croissance</li>
                  <li>✓ Un traducteur business/tech</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary/20 border border-border/50 p-8 lg:p-16 text-center"
          >
            <div className="hero-glow" />
            <div className="relative z-10">
              <Award className="w-12 h-12 text-primary mx-auto mb-6" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Envie de travailler ensemble ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Discutons de votre projet. Un premier échange gratuit pour comprendre 
                vos enjeux et voir si on peut vous aider.
              </p>
              <Link to="/contact">
                <Button variant="hero" size="xl" className="btn-glow group">
                  Prendre rendez-vous
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
